/**
 * Adding files to a deal room that already exists (DOP-608).
 *
 * The backend replaces an attachment in place when a file arrives on a path the
 * room already holds (`overwrite_existing`, keyed on `relative_path`). That is
 * the right repair semantics for a partial upload, but for an analyst adding
 * files later it would silently swap out a document. So before anything is
 * sent, every incoming file is sorted against what the room already has:
 *
 *   - `new`       — no file on that path; it is simply added.
 *   - `changed`   — a file with that path exists and its bytes differ (or we
 *                   cannot tell). The analyst chooses: replace, keep both, skip.
 *   - `duplicate` — a file with that path exists and the hashes match. Sending
 *                   it again would change nothing, so it is left out.
 *   - `checking`  — a match exists with a known hash and ours is still being
 *                   computed. The dialog holds the Add button until it settles.
 *
 * Pure functions only: the dialog's hook owns the state and the hashing.
 */

import type { FileInfo, FolderTreeNode } from "../services/fileApi";
import {
	normalizeRelativePath,
	relativePathFor,
	setRelativePath,
} from "./folderUpload";

export type IncomingStatus = "new" | "changed" | "duplicate" | "checking";
export type ChangedChoice = "replace" | "keep-both" | "skip";

export interface ReviewItem {
	/** The path the file would be stored under — unique within a review. */
	path: string;
	file: File;
	status: IncomingStatus;
	/** The deal-room file on the same path, when there is one. */
	existing?: FileInfo;
}

export interface ReviewSummary {
	added: number;
	replaced: number;
	skipped: number;
	/** Files that will actually be sent. */
	toSend: number;
}

/**
 * The path a stored file occupies, as the backend matches it on overwrite:
 * `relative_path`, falling back to the bare filename for legacy rows.
 */
export const dealFilePath = (
	file: Pick<FileInfo, "relative_path" | "filename">
): string => normalizeRelativePath(file.relative_path || file.filename);

/** Every file in a deal room, keyed by its stored path. */
export const flattenDealFiles = (
	node: FolderTreeNode
): Map<string, FileInfo> => {
	const byPath = new Map<string, FileInfo>();
	const walk = (current: FolderTreeNode) => {
		for (const file of current.files) byPath.set(dealFilePath(file), file);
		current.sub_folders.forEach(walk);
	};
	walk(node);
	return byPath;
};

/** Total files in a deal room, sub-folders included. */
export const countDealFiles = (node: FolderTreeNode): number =>
	node.files.length +
	node.sub_folders.reduce((sum, child) => sum + countDealFiles(child), 0);

/**
 * Merge a newly picked batch into the files already under review. A later
 * pick of the same path replaces the earlier one — it is what the analyst
 * dropped most recently.
 */
export const mergeIncoming = (current: File[], added: File[]): File[] => {
	const byPath = new Map<string, File>();
	for (const file of [...current, ...added]) {
		byPath.set(relativePathFor(file), file);
	}
	return Array.from(byPath.values());
};

/**
 * Sort incoming files against the room. `hashes` holds the SHA-256 of each
 * incoming file that has been hashed; `null` means hashing was attempted and
 * could not finish (too large, unsupported), which reads as "can't tell".
 */
export const classifyIncoming = (
	files: File[],
	existing: Map<string, FileInfo>,
	hashes: Map<File, string | null>
): ReviewItem[] =>
	files.map((file) => {
		const path = relativePathFor(file);
		const match = existing.get(path);
		if (!match) return { path, file, status: "new" };

		const existingHash = match.content_sha256 ?? null;
		if (!existingHash) return { path, file, status: "changed", existing: match };
		if (!hashes.has(file)) {
			return { path, file, status: "checking", existing: match };
		}
		const incomingHash = hashes.get(file);
		return {
			path,
			file,
			status: incomingHash === existingHash ? "duplicate" : "changed",
			existing: match,
		};
	});

/** True when this file needs hashing to be classified. */
export const needsHash = (
	file: File,
	existing: Map<string, FileInfo>
): boolean => Boolean(existing.get(relativePathFor(file))?.content_sha256);

const splitExtension = (name: string): [string, string] => {
	const dot = name.lastIndexOf(".");
	// A leading dot is a hidden-file name, not an extension.
	return dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ""];
};

/**
 * The first free "Name (2).ext" beside `path`. `taken` holds every path the
 * room has plus every path this review will create.
 */
export const keepBothPath = (path: string, taken: Set<string>): string => {
	const slash = path.lastIndexOf("/");
	const dir = slash >= 0 ? path.slice(0, slash + 1) : "";
	const [stem, ext] = splitExtension(path.slice(slash + 1));
	for (let n = 2; ; n += 1) {
		const candidate = `${dir}${stem} (${n})${ext}`;
		if (!taken.has(candidate)) return candidate;
	}
};

const choiceFor = (
	item: ReviewItem,
	choices: Record<string, ChangedChoice>
): ChangedChoice => choices[item.path] ?? "replace";

/**
 * The files to send, in review order. A kept-both file travels as a copy
 * registered under its new path — the part filename is the only channel the
 * backend reads a path from.
 */
export const buildUploadPlan = (
	items: ReviewItem[],
	choices: Record<string, ChangedChoice>,
	existing: Map<string, FileInfo>
): File[] => {
	const taken = new Set<string>([
		...Array.from(existing.keys()),
		...items.map((item) => item.path),
	]);
	const plan: File[] = [];
	for (const item of items) {
		if (item.status === "new") {
			plan.push(item.file);
			continue;
		}
		if (item.status !== "changed") continue;
		const choice = choiceFor(item, choices);
		if (choice === "replace") plan.push(item.file);
		if (choice === "keep-both") {
			const path = keepBothPath(item.path, taken);
			taken.add(path);
			const copy = new File([item.file], path.split("/").pop() ?? path, {
				type: item.file.type,
				lastModified: item.file.lastModified,
			});
			setRelativePath(copy, path);
			plan.push(copy);
		}
	}
	return plan;
};

export const summarizeReview = (
	items: ReviewItem[],
	choices: Record<string, ChangedChoice>
): ReviewSummary => {
	let added = 0;
	let replaced = 0;
	let skipped = 0;
	for (const item of items) {
		if (item.status === "new") added += 1;
		else if (item.status === "duplicate") skipped += 1;
		else if (item.status === "changed") {
			const choice = choiceFor(item, choices);
			if (choice === "replace") replaced += 1;
			else if (choice === "keep-both") added += 1;
			else skipped += 1;
		}
	}
	return { added, replaced, skipped, toSend: added + replaced };
};

/** Largest file hashed in the browser; bigger ones read as "can't tell". */
export const MAX_HASH_BYTES = 200 * 1024 * 1024;

/** Hex SHA-256 of a file, or null when the browser cannot compute it. */
export const sha256Hex = async (file: File): Promise<string | null> => {
	if (file.size > MAX_HASH_BYTES) return null;
	try {
		const subtle = globalThis.crypto?.subtle;
		if (!subtle || typeof file.arrayBuffer !== "function") return null;
		const digest = await subtle.digest("SHA-256", await file.arrayBuffer());
		return Array.from(new Uint8Array(digest))
			.map((byte) => byte.toString(16).padStart(2, "0"))
			.join("");
	} catch {
		return null;
	}
};

/**
 * The deal these files most likely belong to: the one sharing the most paths
 * with them. `null` when no deal shares any.
 */
export const suggestDeal = (
	files: File[],
	deals: FolderTreeNode[]
): { deal: FolderTreeNode; matches: number } | null => {
	const paths = files.map(relativePathFor);
	let best: { deal: FolderTreeNode; matches: number } | null = null;
	for (const deal of deals) {
		const existing = flattenDealFiles(deal);
		const matches = paths.filter((path) => existing.has(path)).length;
		if (matches > 0 && (!best || matches > best.matches)) {
			best = { deal, matches };
		}
	}
	return best;
};
